import {
  Tooltip,
  IconButton,
  IconButtonProps,
  tooltipClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { brand } from "../../theme/themePrimitives";

type Props = {
  to?: string; // route to navigate
  state?: any; // data to pass to the new route
  title: string; // tooltip text
  icon: React.ReactNode; // icon
  onClick?: () => void; // optional click handler for action mode
  target?: "_blank" | "_self"; // target for link
  linkColor?: string;
  disabled?: boolean;
  hidden?: boolean;
} & Omit<IconButtonProps, "children">;

const StyledTooltip = styled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      arrow
      placement="bottom"
    />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    background: brand[1000],
    padding: "8px 16px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: brand[1000],
  },
}));

export function CustomActionIconLink({
  to,
  state,
  title,
  icon,
  onClick,
  target,
  linkColor,
  disabled,
  hidden,
  ...iconButtonProps
}: Props) {
  if (hidden) return null;

  const StyledButton = styled(IconButton)(() => ({
    borderRadius: 12,
    width: 36,
    height: 36,
    "&:hover": {
      borderColor: linkColor ? linkColor : brand[600],
      color: linkColor ? linkColor : brand[600],
    },
  }));

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      onClick();
    }

    // If target is "_blank", open in new tab
    if (target === "_blank" && to) {
      e.preventDefault();
      e.stopPropagation();
      // Use window.open with full URL
      const url = window.location.origin + to;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
  };

  const buttonContent = (
    <StyledTooltip title={title}>
      <StyledButton
        onClick={handleClick}
        {...iconButtonProps}
        disabled={disabled}
      >
        {icon}
      </StyledButton>
    </StyledTooltip>
  );

  if (disabled || !to) {
    return buttonContent;
  }

  // If target is "_blank", don't use Link (use regular button with window.open)
  if (target === "_blank" && to) {
    return buttonContent;
  }

  return to ? (
    <Link to={to} state={state}>
      {buttonContent}
    </Link>
  ) : (
    buttonContent
  );
}
